import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { auth, setToken, getToken, type UserOut } from '../api/client';

interface AuthContextType {
  user: UserOut | null;
  isAdmin: boolean;
  isLoading: boolean;
  login: (credentials: { email?: string; phone?: string; password: string }) => Promise<void>;
  sendRegisterOtp: (data: { email: string; password: string; first_name: string; last_name: string; phone?: string }) => Promise<void>;
  register: (data: { email: string; password: string; first_name: string; last_name: string; phone?: string; otp: string; referral_code?: string }) => Promise<void>;
  resendVerifyOTP: (email: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserOut | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const bootstrap = useCallback(() => {
    const token = getToken();
    if (token) {
      auth.me().then(setUser).catch(() => {
        setToken(null);
        setUser(null);
      }).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (credentials: { email?: string; phone?: string; password: string }) => {
    const { access_token } = await auth.login(credentials);
    setToken(access_token);
    const me = await auth.me();
    setUser(me);
  };

  const sendRegisterOtp = async (data: { email: string; password: string; first_name: string; last_name: string; phone?: string }) => {
    await auth.sendRegisterOtp(data);
  };

  const register = async (data: { email: string; password: string; first_name: string; last_name: string; phone?: string; otp: string; referral_code?: string }) => {
    const { access_token } = await auth.register(data);
    setToken(access_token);
    const me = await auth.me();
    setUser(me);
  };

  const resendVerifyOTP = async (email: string) => {
    await auth.resendVerifyOTP(email);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const me = await auth.me();
    setUser(me);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin: user?.is_admin ?? false, isLoading, login, sendRegisterOtp, register, resendVerifyOTP, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
